import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { EvidenceNotesPanel } from "../components/EvidenceNotesPanel";
import { ReportApprovalActions } from "../components/report/ReportApprovalActions";
import { ReportHeader } from "../components/report/ReportHeader";
import { ReportInputs } from "../components/report/ReportInputs";
import { ReportLinks } from "../components/report/ReportLinks";
import { ReportReferences } from "../components/report/ReportReferences";
import { ReportResultSections } from "../components/report/ReportResultSections";
import { reportSectionOf } from "../components/report/reportSections";
import { ReportSignatures } from "../components/report/ReportSignatures";
import { ReportStatusBlock } from "../components/report/ReportStatusBlock";
import {
  buildReport,
  getReport,
  ReportApiError,
  type ReportDocument,
  type ReportErrorCode,
  type ReportKey,
} from "../services/report";

// The printable report of one stored run (EOS-13, Master Prompt A31; design note section 5).
// Reached at /reports/runs/:runId (the run by id, getReport) or /reports/by-key?module_code=
// ...&calculation_type=...&calculation_key=...[&project_revision_id=...] (the study's approved
// run, else its latest revision - the server decides, buildReport). The page lays out the nine
// sections through the commit 7 blocks and adds nothing of its own to the figures: every value
// is the document's as received. "Print / PDF" is the browser's own print (window.print) to A4
// portrait through styles/report-print.css; an unapproved report carries the DRAFT watermark on
// screen and on every printed page (A31 (d)), an approved one carries none.
//
// Print frame: the browser target has no @page margin boxes, so the running header and footer
// every printed page repeats are position: fixed blocks (styles/report-print.css), and a table
// whose thead and tfoot rows (spacers of their height) repeat on every page keeps the sections
// clear of them. On screen the frame is a plain block and the two bars are not shown.

export const REPORT_ERROR_MESSAGES: Record<ReportErrorCode, string> = {
  UNAUTHENTICATED: "Your session has ended. Sign in again to open this report.",
  FORBIDDEN: "You are not allowed to open this report.",
  NOT_FOUND:
    "No stored run matches this report address, or the run belongs to another organization.",
  VALIDATION: "The report address is not valid",
  CONFLICT: "The report could not be built",
  ALREADY_APPROVED: "The report could not be built",
  ALREADY_REJECTED: "The report could not be built",
  REVISION_ISSUED: "The report could not be built",
  EVIDENCE_CHECKSUM_MISMATCH: "The report could not be built",
  REASON_REQUIRED: "The report could not be built",
  UNEXPECTED_STATUS: "The report could not be loaded",
};

const EMPTY_TEXT =
  "No stored run is named. Open a report from a stored run's traceability panel, or name the " +
  "study by module code, calculation type and calculation key.";

export type ReportTarget =
  | { kind: "run"; runId: string }
  | { kind: "key"; key: ReportKey }
  | { kind: "none" };

/** What the address names: a run by id, a study by key, or nothing the page can load. */
export function reportTarget(runId: string | undefined, search: URLSearchParams): ReportTarget {
  if (runId !== undefined && runId.trim() !== "") {
    return { kind: "run", runId };
  }
  const module_code = search.get("module_code");
  const calculation_type = search.get("calculation_type");
  const calculation_key = search.get("calculation_key");
  if (!module_code || !calculation_type || !calculation_key) {
    return { kind: "none" };
  }
  const project_revision_id = search.get("project_revision_id");
  return {
    kind: "key",
    key: {
      module_code,
      calculation_type,
      calculation_key,
      ...(project_revision_id ? { project_revision_id } : {}),
    },
  };
}

/** The sentence shown for a failed load, with the code a test or a style can read. */
export function describeReportFailure(error: unknown): { code: string; message: string } {
  if (error instanceof ReportApiError) {
    const lead = REPORT_ERROR_MESSAGES[error.code];
    switch (error.code) {
      case "UNAUTHENTICATED":
      case "FORBIDDEN":
      case "NOT_FOUND":
        return { code: error.code, message: lead };
      case "UNEXPECTED_STATUS":
        return { code: error.code, message: `${lead} (HTTP ${error.status}): ${error.message}` };
      default:
        return { code: error.code, message: `${lead}: ${error.message}` };
    }
  }
  const detail = error instanceof Error && error.message.trim() !== "" ? ` ${error.message}` : "";
  return { code: "UNKNOWN", message: `The report could not be loaded.${detail}` };
}

/** True while the browser prints (beforeprint / afterprint) or shows the print media. */
function usePrinting(): boolean {
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    const start = () => setPrinting(true);
    const stop = () => setPrinting(false);
    window.addEventListener("beforeprint", start);
    window.addEventListener("afterprint", stop);

    const media = typeof window.matchMedia === "function" ? window.matchMedia("print") : null;
    const onMedia = (event: MediaQueryListEvent) => setPrinting(event.matches);
    if (media !== null) {
      setPrinting(media.matches);
      media.addEventListener("change", onMedia);
    }
    return () => {
      window.removeEventListener("beforeprint", start);
      window.removeEventListener("afterprint", stop);
      media?.removeEventListener("change", onMedia);
    };
  }, []);

  return printing;
}

function runningHeaderText(document: ReportDocument): string {
  const { header, module, status } = document;
  const project =
    header.project_code === null
      ? "Unassigned - no project"
      : `${header.project_code} — ${header.project_name ?? "—"}`;
  const revision = header.revision_label === null ? "—" : header.revision_label;
  const state = status.is_draft ? "DRAFT — not approved" : "Approved";
  return `${project} · ${revision} · ${state} · ${module.calculation_key} · revision ${module.revision_number}`;
}

type ReportSheetProps = {
  document: ReportDocument;
  printing: boolean;
};

function ReportSheet({ document, printing }: ReportSheetProps) {
  const labels = reportSectionOf(document.module.calculation_type)?.inputLabels ?? { fields: {} };
  const draft = document.status.is_draft;

  return (
    <article
      data-report
      data-report-draft={draft ? "true" : "false"}
      data-run-id={document.run_id}
      aria-label="Calculation report"
    >
      {draft ? (
        <div data-report-watermark aria-hidden="true">
          DRAFT
        </div>
      ) : null}
      <div data-report-print-header aria-hidden="true">
        {runningHeaderText(document)}
      </div>
      <div data-report-print-footer aria-hidden="true">
        Content hash (SHA-256) <code>{document.content_hash}</code> · a design check, not a
        compliance statement · KES Electrical OS
      </div>
      <table data-report-frame role="presentation">
        <thead>
          <tr>
            <td>
              <div data-report-frame-spacer="top" />
            </td>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <td>
              <div data-report-frame-spacer="bottom" />
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td>
              <ReportHeader document={document} />
              <ReportStatusBlock document={document} />
              <EvidenceNotesPanel notes={document.evidence_notes ?? []} reportSection />
              <ReportResultSections document={document} />
              <ReportInputs snapshot={document.input_snapshot} labels={labels} open={printing} />
              <ReportLinks document={document} />
              <ReportReferences document={document} />
              <ReportSignatures document={document} />
            </td>
          </tr>
        </tbody>
      </table>
    </article>
  );
}

export function RunReportPage() {
  const { runId } = useParams<{ runId: string }>();
  const [search] = useSearchParams();
  const target = reportTarget(runId, search);
  const printing = usePrinting();

  const query = useQuery({
    queryKey: ["report", target],
    queryFn: ({ signal }) =>
      target.kind === "run"
        ? getReport(target.runId, signal)
        : buildReport((target as { kind: "key"; key: ReportKey }).key, signal),
    enabled: target.kind !== "none",
  });

  const document = query.data;

  return (
    <main data-report-page>
      <header>
        <p>EOS-13 · Deliverables</p>
        <h1>
          {document
            ? `${document.module.calculation_key} · revision ${document.module.revision_number}`
            : "Calculation report"}
        </h1>
        <p>
          A printable report of one stored run: every figure, warning and reference is read from
          the run as stored. Nothing is recalculated.
        </p>
      </header>

      {target.kind === "none" ? (
        <p data-report-state="empty">{EMPTY_TEXT}</p>
      ) : query.isPending ? (
        <p role="status" data-report-state="loading">
          Loading the report…
        </p>
      ) : query.isError ? (
        <div
          role="alert"
          data-report-state="error"
          data-report-error={describeReportFailure(query.error).code}
        >
          <p>{describeReportFailure(query.error).message}</p>
        </div>
      ) : document ? (
        <>
          <p data-report-actions>
            <button type="button" onClick={() => window.print()}>
              Print / PDF
            </button>
          </p>
          {/* The OWNER's approve / reject actions (D8); nothing for other roles or an approved run. */}
          <ReportApprovalActions document={document} />
          <ReportSheet document={document} printing={printing} />
        </>
      ) : null}
    </main>
  );
}

export default RunReportPage;
